/**
 * Client-side routing simulation (ported from prototype, CHC-863 §3.3).
 *
 * Mirrors the production routing rule as understood from the contract:
 *   the payment goes to the FIRST enabled adapter, in priority order
 *   (array order), whose [minDeposit, maxDeposit] range contains the amount.
 *
 * No real routing engine and no API are involved — this is a pure projection
 * of the current UI state used to preview the effect of a configuration.
 *
 * Product assumption (hypothesis): a "hole" in coverage means the payment is
 * not routable. Overlaps are allowed; the higher-priority adapter wins, the
 * overlap is surfaced only as an informational warning.
 */

import type { AdapterRow } from './types'

export type RouteOutcome =
  | { routed: true; adapter: AdapterRow; rank: number; reason: string }
  | { routed: false; reason: string }

/**
 * Returns whether an amount falls within an adapter's [min, max] range.
 * Bounds are inclusive. An invalid range (min > max) never matches.
 */
export function inRange(
  amount: number,
  row: Pick<AdapterRow, 'minDeposit' | 'maxDeposit'>
): boolean {
  if (row.minDeposit > row.maxDeposit) return false
  return amount >= row.minDeposit && amount <= row.maxDeposit
}

/**
 * Simulate routing of a single amount through the ordered adapter list.
 * The array order is the priority (index 0 == rank 1).
 */
export function routeAmount(amount: number, adapters: AdapterRow[]): RouteOutcome {
  if (!Number.isFinite(amount) || amount < 0) {
    return { routed: false, reason: 'Введите корректную сумму (число ≥ 0).' }
  }

  for (let i = 0; i < adapters.length; i++) {
    const row = adapters[i]
    if (!row.enabled || !row.globallyEnabled) continue
    if (inRange(amount, row)) {
      return {
        routed: true,
        adapter: row,
        rank: i + 1,
        reason:
          `Первый включённый адаптер по приоритету (ранг ${i + 1}), ` +
          `чей диапазон ${fmt(row.minDeposit)}–${fmt(row.maxDeposit)} ₽ содержит сумму.`,
      }
    }
  }

  return {
    routed: false,
    reason:
      'Ни один включённый адаптер не покрывает эту сумму — платёж не маршрутизируется (дыра в покрытии).',
  }
}

export type CoverageKind = 'covered' | 'hole' | 'overlap'

export interface CoverageSegment {
  /** Inclusive start of the segment. */
  from: number
  /** Inclusive end; `null` means open-ended (to +∞). */
  to: number | null
  kind: CoverageKind
  /** Adapter (by id) that would actually win this segment, if covered. */
  winnerId: number | null
  winnerName: string | null
  winnerRank: number | null
  /** All enabled adapters whose range covers this segment (>1 => overlap). */
  adapterIds: number[]
}

/**
 * Build a coverage map across the amount axis from the current adapter list.
 *
 * Only enabled + globally-enabled adapters with a valid range participate.
 * The axis is segmented at every distinct boundary (min and max+1); each
 * segment is classified as covered (1 adapter), overlap (>1), or hole (0).
 * The winner of a covered/overlap segment is the highest-priority adapter.
 */
export function buildCoverage(adapters: AdapterRow[]): CoverageSegment[] {
  const active = adapters
    .map((row, index) => ({ row, rank: index + 1 }))
    .filter(
      ({ row }) => row.enabled && row.globallyEnabled && row.minDeposit <= row.maxDeposit
    )

  if (active.length === 0) {
    return [
      { from: 0, to: null, kind: 'hole', winnerId: null, winnerName: null, winnerRank: null, adapterIds: [] },
    ]
  }

  // Collect breakpoints: every min, and every (max + 1) as the start of the
  // next segment. We treat amounts as integers (kopecks ignored in the pilot).
  const breakpoints = new Set<number>([0])
  for (const { row } of active) {
    breakpoints.add(row.minDeposit)
    breakpoints.add(row.maxDeposit + 1)
  }
  const sorted = Array.from(breakpoints).sort((a, b) => a - b)

  const segments: CoverageSegment[] = []
  for (let i = 0; i < sorted.length; i++) {
    const from = sorted[i]
    const nextBoundary = sorted[i + 1]
    const to = nextBoundary === undefined ? null : nextBoundary - 1

    // Adapters covering the representative point `from` of this segment.
    const covering = active.filter(({ row }) => inRange(from, row))
    const ids = covering.map(({ row }) => row.adapterId)

    let kind: CoverageKind
    let winner: (typeof active)[number] | null = null
    if (covering.length === 0) kind = 'hole'
    else {
      kind = covering.length > 1 ? 'overlap' : 'covered'
      // Highest priority (lowest rank) wins.
      winner = covering.reduce((best, cur) => (cur.rank < best.rank ? cur : best))
    }

    segments.push({
      from,
      to,
      kind,
      winnerId: winner?.row.adapterId ?? null,
      winnerName: winner?.row.name ?? null,
      winnerRank: winner?.rank ?? null,
      adapterIds: ids,
    })
  }

  return mergeAdjacent(segments)
}

/** Collapse adjacent segments that classify identically (same kind + winner). */
function mergeAdjacent(segments: CoverageSegment[]): CoverageSegment[] {
  const out: CoverageSegment[] = []
  for (const seg of segments) {
    const prev = out[out.length - 1]
    if (
      prev &&
      prev.kind === seg.kind &&
      prev.winnerId === seg.winnerId &&
      sameIds(prev.adapterIds, seg.adapterIds) &&
      prev.to !== null &&
      prev.to + 1 === seg.from
    ) {
      prev.to = seg.to
    } else {
      out.push({ ...seg })
    }
  }
  return out
}

function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()
  return sa.every((v, i) => v === sb[i])
}

/**
 * Collapse a coverage map for the textual breakdown, where the only thing the
 * reader sees per segment is the *displayed label* — kind + winning adapter.
 */
export function collapseForDisplay(segments: CoverageSegment[]): CoverageSegment[] {
  const out: CoverageSegment[] = []
  for (const seg of segments) {
    const prev = out[out.length - 1]
    if (
      prev &&
      prev.kind === seg.kind &&
      prev.winnerId === seg.winnerId &&
      prev.to !== null &&
      prev.to + 1 === seg.from
    ) {
      prev.to = seg.to
    } else {
      out.push({ ...seg })
    }
  }
  return out
}

function fmt(n: number): string {
  return n.toLocaleString('ru-RU')
}
